import { Notification } from "@phosphor-icons/react";
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";

import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { graphql } from "@/gen";
import { NestedFeedRecord } from "@/hooks/useRealtimeGame";
import { zFeedRecordRichText } from "@/routes/game-feed/types";

const sendFeedRecordNotificationMutation = graphql(`
    mutation SendFeedRecordNotificationMutation($input: SendFeedRecordNotificationInput!) {
        sendFeedRecordNotification(input: $input) {
            id
            title
            body
            attemptedDeliveries
            successfulDeliveries
        }
    }
`);

const feedRecordNotificationQuery = graphql(`
    query FeedRecordNotificationQuery($id: UUID!) {
        feedRecord(id: $id) {
            notification {
                id
                title
                body
                attemptedDeliveries
                successfulDeliveries
            }
        }
    }
`);

type PushMessagePayload = {
    title: string;
    body: string;
};
type SendPushMessageFormProps = {
    feedRecord: NestedFeedRecord;
    onSend: (data: PushMessagePayload) => void;
    isSubmitting: boolean;
};
function SendPushMessageForm({ feedRecord, onSend, isSubmitting }: SendPushMessageFormProps) {
    const suggestedNotificationContent = useFeedRecordNotificationSuggestions(feedRecord);
    const [title, setTitle] = useState(suggestedNotificationContent.title || "");
    const [body, setBody] = useState(suggestedNotificationContent.body || "");

    const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        onSend({ title, body });
    };

    return (
        <>
            <div className="grid gap-4 py-4">
                <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="title" className="text-right">
                        Title
                    </Label>
                    <Input
                        autoFocus
                        id="title"
                        placeholder="Very short title"
                        className="col-span-3"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        disabled={isSubmitting}
                    />
                </div>
                <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="body" className="text-right">
                        Body
                    </Label>
                    <Input
                        id="body"
                        placeholder="Fairly short message body"
                        className="col-span-3"
                        value={body}
                        onChange={e => setBody(e.target.value)}
                        disabled={isSubmitting}
                    />
                </div>
            </div>
            <DialogFooter>
                <Button type="submit" onClick={handleSubmit} disabled={isSubmitting}>
                    {isSubmitting ? (
                        <>
                            <Spinner /> Sending...
                        </>
                    ) : (
                        "Send notification"
                    )}
                </Button>
            </DialogFooter>
        </>
    );
}

type SendRecordPushDialogProps = {
    feedRecord: NestedFeedRecord;
};
export function SendRecordPushDialog({ feedRecord }: SendRecordPushDialogProps) {
    const [{ data }] = useQuery({
        query: feedRecordNotificationQuery,
        variables: { id: feedRecord.id },
    });
    const existingNotification = data?.feedRecord.notification;

    const [mutationState, sendPushNotification] = useMutation(sendFeedRecordNotificationMutation);
    const sentNotification = mutationState.data?.sendFeedRecordNotification;

    const handleSubmit = useCallback(
        async (payload: PushMessagePayload) => {
            const input = {
                id: feedRecord.id,
                ...payload,
            };
            await sendPushNotification({ input });
        },
        [feedRecord.id, sendPushNotification]
    );

    const notification = sentNotification || existingNotification;

    return (
        <DialogContent>
            <DialogHeader>
                <DialogTitle>Feed record notification</DialogTitle>
                <DialogDescription>
                    {notification
                        ? "A notification has been sent."
                        : "Send a push notification to followers of this game."}
                </DialogDescription>
            </DialogHeader>
            {notification ? (
                <>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Title</Label>
                            <span className="col-span-3">{notification.title}</span>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Body</Label>
                            <span className="col-span-3">{notification.body}</span>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Attempted deliveries</Label>
                            <span className="col-span-3">{notification.attemptedDeliveries}</span>
                        </div>
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label className="text-right">Successful deliveries</Label>
                            <span className="col-span-3">{notification.successfulDeliveries}</span>
                        </div>
                    </div>
                    <DialogFooter>
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                Close
                            </Button>
                        </DialogClose>
                    </DialogFooter>
                </>
            ) : (
                <SendPushMessageForm
                    feedRecord={feedRecord}
                    onSend={handleSubmit}
                    isSubmitting={mutationState.fetching}
                />
            )}
        </DialogContent>
    );
}

export function SendRecordPushDialogTrigger({ feedRecord }: SendRecordPushDialogProps) {
    return (
        <Dialog>
            <DialogTrigger asChild>
                <a className="cursor-pointer p-2">
                    <Notification />
                </a>
            </DialogTrigger>
            <SendRecordPushDialog feedRecord={feedRecord} />
        </Dialog>
    );
}

function useFeedRecordNotificationSuggestions(feedRecord: NestedFeedRecord) {
    return useMemo(() => {
        const items = feedRecord.content ? zFeedRecordRichText.parse(feedRecord.content) : [];

        const suggestedTitle =
            items.find(el => el.type === "heading" || el.type === "super-heading")?.children?.[0]
                ?.text || "";
        const suggestedBody = items.find(el => el.type === "paragraph")?.children?.[0]?.text || "";

        return {
            title: suggestedTitle,
            body: suggestedBody,
        } as const;
    }, [feedRecord.content]);
}
